import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Chip,
  Grid,
  Card,
  CardContent,
  Avatar,
  Divider,
  LinearProgress,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import InventoryIcon from "@mui/icons-material/Inventory";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import TimelineIcon from "@mui/icons-material/Timeline";
import SettingsIcon from "@mui/icons-material/Settings";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/Error";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { styled, useTheme } from "@mui/material/styles";

import { useForecast } from "../../context/forecastContext";
import { toast } from "react-toastify";
import InHouseForecastReport from "./in-house-forecast-report";
import { formatDate } from "../../utils/formatDate";

// `input.warehouse` / `input.channel` are multi-select now — a run can carry
// several of either. Render whatever's there (old scalar data included) as a
// comma-joined label.
const warehouseName = (w) =>
  !w ? "" : typeof w === "string" ? w : w.name || w.warehouse_code || "";
const joinedLabel = (value, toLabel = (v) => v) =>
  (Array.isArray(value) ? value : value ? [value] : [])
    .map(toLabel)
    .filter(Boolean)
    .join(", ") || "N/A";

const MetricCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  background: `linear-gradient(135deg, ${theme.palette["secondary"].main}08, ${theme.palette["secondary"].main}04)`,
  borderLeft: `3px solid ${theme.palette["secondary"].main}`,
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: theme.shadows[2],
  },
}));

const UrgentReorderCard = styled(Paper)(({
  theme,
  urgencyLevel = "normal",
}) => {
  const getUrgencyStyle = () => {
    if (urgencyLevel === "critical") {
      return {
        borderLeft: `4px solid ${theme.palette.error.main}`,
        background: `linear-gradient(135deg, ${theme.palette.error.main}12, ${theme.palette.error.main}06)`,
        boxShadow: `0 0 20px ${theme.palette.error.main}20`,
      };
    } else if (urgencyLevel === "warning") {
      return {
        borderLeft: `4px solid ${theme.palette.warning.main}`,
        background: `linear-gradient(135deg, ${theme.palette.warning.main}12, ${theme.palette.warning.main}06)`,
        boxShadow: `0 0 16px ${theme.palette.warning.main}18`,
      };
    }
    return {
      borderLeft: `3px solid ${theme.palette.success.main}`,
      background: `linear-gradient(135deg, ${theme.palette.success.main}08, ${theme.palette.success.main}04)`,
    };
  };

  return {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[4],
    },
    ...getUrgencyStyle(),
  };
});

const InputParamCard = ({ label, value }) => {
  const theme = useTheme();
  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: "var(--bg-highlight)",
        flex: "1 1 auto",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontFamily: "Poppins",
          fontWeight: 600,
          color: theme.palette.text.secondary,
          fontSize: "10px",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          display: "block",
          mb: 1,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontFamily: "Poppins",
          fontWeight: 600,
          color: theme.palette.primary.main,
          fontSize: "14px",
        }}
      >
        {value}
      </Typography>
    </Paper>
  );
};

// Helper function to calculate days until reorder and urgency level
const calculateReorderUrgency = (reorderByDate) => {
  if (!reorderByDate) return { daysUntil: null, urgency: "none", label: "" };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const reorderDate = new Date(reorderByDate);
  reorderDate.setHours(0, 0, 0, 0);

  const timeDiff = reorderDate - today;
  const daysUntil = Math.ceil(timeDiff / (1000 * 3600 * 24));

  let urgency = "normal";
  let label = "";

  if (daysUntil < 0) {
    urgency = "overdue";
    label = `OVERDUE (${Math.abs(daysUntil)} days ago)`;
  } else if (daysUntil === 0) {
    urgency = "critical";
    label = "TODAY";
  } else if (daysUntil <= 3) {
    urgency = "critical";
    label = `${daysUntil} day${daysUntil > 1 ? "s" : ""} remaining`;
  } else if (daysUntil <= 7) {
    urgency = "warning";
    label = `${daysUntil} days remaining`;
  } else {
    urgency = "normal";
    label = `${daysUntil} days remaining`;
  }

  return { daysUntil, urgency, label };
};

const ForecastResultModal = () => {
  const {
    open,
    data: forecastData,
    loading = false,
    sku,
    expectedTime,
    closeForecast,
    closeAndDiscard,
    closeAndDiscardAll,
    onAcceptMetrics,
    onAcceptAllMetrics,
    forecastedSKU,
    loadDataBySku,
    showExplanation,
    forecastProductName,
    forecastProductNames,
    acceptMetricsSilent,
    discardMetricsSilent,
    forecastType,
    forecastItems,
    forecastBy,
  } = useForecast();

  const onClose = () => {
    if (!forecastData) {
      closeForecast();
      return;
    }
    if (Array.isArray(forecastData)) {
      closeAndDiscardAll(forecastData);
    } else {
      closeAndDiscard(forecastData);
    }
  };
  const theme = useTheme();
  // `currentLabel` is what appears in the header subtitle during loading and after
  const [currentLabel, setCurrentLabel] = React.useState(null);
  const isProductLoading =
    forecastProductNames && forecastProductNames.length > 0;

  const skuList = Array.isArray(forecastedSKU) ? forecastedSKU : [];
  // Keep currentSku for SKU navigation (non-product forecasts)
  const [currentSku, setCurrentSku] = React.useState(null);
  const currentIndex = skuList.findIndex((item) => item?.sku === currentSku);
  const displayIndex = currentIndex >= 0 ? currentIndex : 0;
  const totalSkus = skuList.length || 0;
  const currentForecastItem = forecastItems.find(
    (item) => item.sku === currentSku,
  );
  const currentProductName =
    currentForecastItem?.product?.name || currentForecastItem?.name;

  useEffect(() => {
    let timer;
    const labels = isProductLoading
      ? forecastProductNames
      : Array.isArray(sku)
        ? sku.filter(Boolean)
        : sku
          ? [sku]
          : [];

    if (loading) {
      if (labels.length <= 1) {
        setCurrentLabel(labels[0] ?? null);
        if (!isProductLoading) setCurrentSku(labels[0] ?? null);
      } else {
        let idx = 0;
        setCurrentLabel(labels[0]);
        if (!isProductLoading) setCurrentSku(labels[0]);
        const interval = setInterval(
          () => {
            idx = (idx + 1) % labels.length;
            setCurrentLabel(labels[idx]);
            if (!isProductLoading) setCurrentSku(labels[idx]);
          },
          Math.round((Number(expectedTime || 60) / labels.length) * 1000),
        );
        timer = interval;
      }
    } else {
      if (isProductLoading) {
        setCurrentLabel(forecastProductNames[0] ?? null);
      } else {
        const skuVal = Array.isArray(sku) ? sku[0] : sku;
        setCurrentSku(skuVal ?? null);
        setCurrentLabel(skuVal ?? null);
      }
    }
    return () => clearInterval(timer);
  }, [sku, expectedTime, loading, forecastProductNames]);

  const [currentStep, setCurrentStep] = React.useState(0);
  const [stepProgress, setStepProgress] = React.useState(0);
  const [activeTab, setActiveTab] = React.useState(0);
  const [displayExplanation, setDisplayExplanation] = React.useState(false);
  const [productViewMode, setProductViewMode] = React.useState("product"); // "product" | "sku"

  const forecastSteps = [
    {
      label: "Pulling Historical Records",
      description: "Retrieving past sales and inventory data",
    },
    {
      label: "Fetching Supplier Config",
      description: "Loading supplier and product configurations",
    },
    {
      label: "Creating Forecast Request",
      description: "Preparing data for AI model processing",
    },
    {
      label: "Processing Forecast Model",
      description: "Running AI forecast algorithms",
    },
    {
      label: "Generating Metrics",
      description: "Analyzing results with AI model",
    },
    {
      label: "Structuring Results",
      description: "Formatting forecast data for display",
    },
  ];

  const [activeProductTab, setActiveProductTab] = React.useState(0);
  const [productViewModes, setProductViewModes] = React.useState({}); // keyed by product tab index

  // Derive validDataArray here (before effects that reference it)

  // Local remaining items — removed as user accepts/discards.
  const [remainingItems, setRemainingItems] = React.useState([]);

  const removeItemsFromRemaining = React.useCallback((itemsToRemove) => {
    console.log("remaining", remainingItems);

    const ids = new Set(
      itemsToRemove.map((i) => i.forecast_data_id).filter(Boolean),
    );
    console.log(ids);

    setRemainingItems((prev) =>
      prev.filter((i) => !ids.has(i.forecast_data_id)),
    );
  }, []);

  React.useEffect(() => {
    setProductViewMode("product");
    setActiveTab(0);
    setActiveProductTab(0);
    setProductViewModes({});
    const dataArray = Array.isArray(forecastData)
      ? forecastData
      : [forecastData];
    const validDataArray = dataArray.filter(
      (item) => item && typeof item === "object",
    );
    console.log("validDataArray", validDataArray);
    setRemainingItems(validDataArray);
  }, [forecastData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close when all items have been acted on; clamp tabs to valid range
  React.useEffect(() => {
    if (remainingItems.length === 0 && !loading) {
      closeForecast();
      return;
    }
    if (productGroups && productGroups.length > 0) {
      setActiveProductTab((prev) => Math.min(prev, productGroups.length - 1));
    }
    setActiveTab(0);
  }, [remainingItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // Simulate step progression during loading - more gradual and realistic
  React.useEffect(() => {
    if (!loading) return;

    setCurrentStep(0);
    setStepProgress(0);

    const totalSteps = forecastSteps.length || 1;
    const totalMs = Math.max(1000, Number(expectedTime || 120) * 1000);
    const stepDuration = totalMs / totalSteps; // ms per step
    const updateInterval = 1000; // ms

    const interval = setInterval(() => {
      setStepProgress((prevProgress) => {
        const increment = 100 / (stepDuration / updateInterval);
        const newProgress = prevProgress + increment;

        if (newProgress >= 100) {
          setCurrentStep((prevStep) => {
            if (prevStep >= totalSteps - 1) {
              // If we're on the last step, keep it active and let progress cycle
              return prevStep;
            }
            return prevStep + 1;
          });
          // reset progress to 0 to start next step (or to keep cycling last step)
          return 0;
        }

        return newProgress;
      });
    }, updateInterval);

    return () => clearInterval(interval);
  }, [loading, expectedTime]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatMetric = (value, label) => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value === "number") {
      if (label?.toLowerCase().includes("date")) {
        return formatDate(value);
      }
      if (label?.toLowerCase().includes("demand")) {
        return isNaN(value)
          ? (value || "0").toLocaleString()
          : Number(value).toFixed(2);
      }
      return isNaN(value)
        ? (value || "0").toLocaleString()
        : Math.round(Number(value));
    }
    return value;
  };

  const renderForecastCard = (item, index) => {
    // Add null check for item
    if (!item || typeof item !== "object") {
      return (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            Invalid forecast data
          </Typography>
        </Box>
      );
    }

    return (
      <Card
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 4,
          mb: 2,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {item.engine === "in_house" ? (
            <InHouseForecastReport item={item} />
          ) : (
            <>
          {item.metrics && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <AssessmentIcon sx={{ fontSize: 20 }} />
                Key Metrics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard color="primary">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <InventoryIcon
                        sx={{
                          fontSize: 20,
                          color: theme.palette.text.primary,
                        }}
                      />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            fontSize: "11px",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            lineHeight: 1,
                          }}
                        >
                          On Hand
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                            fontSize: "18px",
                            lineHeight: 1,
                          }}
                        >
                          {formatMetric(item.metrics.total_on_hand)}
                        </Typography>
                      </Box>
                    </Box>
                  </MetricCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard color="secondary">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <TrendingUpIcon
                        sx={{
                          fontSize: 20,
                          color: theme.palette.text.secondary,
                        }}
                      />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            fontSize: "11px",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            lineHeight: 1,
                          }}
                        >
                          Avg Daily Demand
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 700,
                            color: theme.palette.text.secondary,
                            fontSize: "18px",
                            lineHeight: 1,
                          }}
                        >
                          {formatMetric(
                            item.metrics.avg_daily_demand,
                            "demand",
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </MetricCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard color="success">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            fontSize: "11px",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            lineHeight: 1,
                          }}
                        >
                          Safety Stock
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 700,
                            color: theme.palette.text.secondary,
                            fontSize: "18px",
                            lineHeight: 1,
                          }}
                        >
                          {item.metrics.safety_stock_used
                            ? formatMetric(item.metrics.safety_stock_used)
                            : "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  </MetricCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard color="error">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <ScheduleIcon
                        sx={{
                          fontSize: 20,
                          color: theme.palette.text.secondary,
                        }}
                      />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            fontSize: "11px",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            lineHeight: 1,
                          }}
                        >
                          Lead Time
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 700,
                            color: theme.palette.text.secondary,
                            fontSize: "18px",
                            lineHeight: 1,
                          }}
                        >
                          {item.metrics.lead_time_days
                            ? formatMetric(item.metrics.lead_time_days) +
                              " days"
                            : "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  </MetricCard>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard color="info">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <TrendingUpIcon
                        sx={{
                          fontSize: 20,
                          color: theme.palette.text.secondary,
                        }}
                      />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            fontSize: "11px",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            lineHeight: 1,
                          }}
                        >
                          In Transit
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 700,
                            color: theme.palette.text.secondary,
                            fontSize: "18px",
                            lineHeight: 1,
                          }}
                        >
                          {formatMetric(item.metrics.total_in_transit)}
                        </Typography>
                      </Box>
                    </Box>
                  </MetricCard>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard color="success">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <ShoppingCartIcon
                        sx={{
                          fontSize: 20,
                          color: theme.palette.text.secondary,
                        }}
                      />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            fontSize: "11px",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            lineHeight: 1,
                          }}
                        >
                          Recomended Reorder Quantity
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 700,
                            color: theme.palette.text.secondary,
                            fontSize: "18px",
                            lineHeight: 1,
                          }}
                        >
                          {formatMetric(item.metrics.recommended_order_qty)}
                        </Typography>
                      </Box>
                    </Box>
                  </MetricCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard color="warning">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            fontSize: "11px",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            lineHeight: 1,
                          }}
                        >
                          Reorder Point
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 700,
                            color: theme.palette.text.secondary,
                            fontSize: "18px",
                            lineHeight: 1,
                          }}
                        >
                          {formatMetric(item.metrics.reorder_point)}
                        </Typography>
                      </Box>
                    </Box>
                  </MetricCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  {item.metrics.reorder_by_date ? (
                    (() => {
                      const urgencyInfo = calculateReorderUrgency(
                        item.metrics.reorder_by_date,
                      );
                      const isUpcoming =
                        urgencyInfo.urgency !== "normal" &&
                        urgencyInfo.urgency !== "none";

                      return (
                        <UrgentReorderCard
                          urgencyLevel={
                            isUpcoming ? urgencyInfo.urgency : "normal"
                          }
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 48,
                                height: 48,
                                borderRadius: "50%",
                                background:
                                  urgencyInfo.urgency === "critical"
                                    ? `${theme.palette.error.main}20`
                                    : urgencyInfo.urgency === "warning"
                                      ? `${theme.palette.warning.main}20`
                                      : `${theme.palette.success.main}20`,
                              }}
                            >
                              {urgencyInfo.urgency === "critical" ? (
                                <ErrorIcon
                                  sx={{
                                    color: theme.palette.error.main,
                                    fontSize: 24,
                                    fontWeight: 700,
                                  }}
                                />
                              ) : urgencyInfo.urgency === "warning" ? (
                                <WarningAmberIcon
                                  sx={{
                                    color: theme.palette.warning.main,
                                    fontSize: 24,
                                  }}
                                />
                              ) : (
                                <CalendarTodayIcon
                                  sx={{
                                    color: theme.palette.success.main,
                                    fontSize: 24,
                                  }}
                                />
                              )}
                            </Box>

                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontFamily: "Poppins",
                                  fontWeight: 700,
                                  color:
                                    urgencyInfo.urgency === "critical"
                                      ? theme.palette.error.main
                                      : urgencyInfo.urgency === "warning"
                                        ? theme.palette.warning.main
                                        : theme.palette.success.main,
                                  fontSize: "10px",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.8,
                                  lineHeight: 1,
                                  display: "block",
                                  mb: 0.5,
                                }}
                              >
                                {urgencyInfo.urgency === "critical"
                                  ? "⚠ Action Required"
                                  : urgencyInfo.urgency === "warning"
                                    ? "⏰ Upcoming"
                                    : "Next Reorder"}
                              </Typography>

                              <Typography
                                variant="h6"
                                sx={{
                                  fontFamily: "Poppins",
                                  fontWeight: 800,
                                  color:
                                    urgencyInfo.urgency === "critical"
                                      ? theme.palette.error.main
                                      : urgencyInfo.urgency === "warning"
                                        ? theme.palette.warning.main
                                        : theme.palette.text.secondary,
                                  fontSize: "16px",
                                  lineHeight: 1.2,
                                  mb: 0.5,
                                }}
                              >
                                {new Date(
                                  item.metrics.reorder_by_date,
                                )?.toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "2-digit",
                                  year: "numeric",
                                })}
                              </Typography>

                              {isUpcoming && (
                                <Chip
                                  label={urgencyInfo.label}
                                  size="small"
                                  icon={
                                    urgencyInfo.urgency === "critical" ? (
                                      <ErrorIcon sx={{ fontSize: 14 }} />
                                    ) : (
                                      <WarningAmberIcon sx={{ fontSize: 14 }} />
                                    )
                                  }
                                  sx={{
                                    height: 20,
                                    fontSize: "11px",
                                    fontFamily: "Poppins",
                                    fontWeight: 700,
                                    bgcolor:
                                      urgencyInfo.urgency === "critical"
                                        ? `${theme.palette.error.main}15`
                                        : `${theme.palette.warning.main}15`,
                                    color:
                                      urgencyInfo.urgency === "critical"
                                        ? theme.palette.error.main
                                        : theme.palette.warning.main,
                                    border: `1px solid ${
                                      urgencyInfo.urgency === "critical"
                                        ? theme.palette.error.main
                                        : theme.palette.warning.main
                                    }`,
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </UrgentReorderCard>
                      );
                    })()
                  ) : (
                    <MetricCard>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <CalendarTodayIcon
                          sx={{
                            fontSize: 20,
                            color: theme.palette.text.secondary,
                          }}
                        />
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 600,
                              color: theme.palette.text.secondary,
                              fontSize: "11px",
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              lineHeight: 1,
                            }}
                          >
                            Next Reorder Date
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 700,
                              color: theme.palette.text.secondary,
                              fontSize: "18px",
                              lineHeight: 1,
                            }}
                          >
                            N/A
                          </Typography>
                        </Box>
                      </Box>
                    </MetricCard>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
          {/* Input Parameters */}
          {item.input && (
            <Accordion
              defaultExpanded={false}
              sx={{
                boxShadow: "none",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                "&:before": {
                  display: "none",
                },
                "&.Mui-expanded": {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: 56,
                  "&.Mui-expanded": {
                    minHeight: 56,
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "16px 0",
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <SettingsIcon sx={{ fontSize: 20 }} />
                  Forecast Parameters
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3, pt: 0 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={2}>
                    <InputParamCard
                      label="Forecast By"
                      value={item.input.forecast_by || "sku"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <InputParamCard
                      label="Type"
                      value={item.input.type || "Monthly"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <InputParamCard
                      label="Horizon"
                      value={`${item.input.horizon} periods`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <InputParamCard
                      label="Warehouse"
                      value={joinedLabel(item.input?.warehouse, warehouseName)}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <InputParamCard
                      label="Channel"
                      value={joinedLabel(item.input?.channel)}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <InputParamCard
                      label="Discount Percentage"
                      value={
                        item.input.discount != null
                          ? `${item.input.discount}%`
                          : "N/A"
                      }
                    />
                  </Grid>
                  {item.input.forecast_by?.toLowerCase() === "product" ? (
                    <Grid item xs={12} sm={6} md={2}>
                      <InputParamCard
                        label="Product"
                        value={item.input.product?.name || "N/A"}
                      />
                    </Grid>
                  ) : item.input.forecast_by?.toLowerCase() === "category" ? (
                    <Grid item xs={12} sm={6} md={2}>
                      <InputParamCard
                        label="Category"
                        value={
                          item.input.category ||
                          item.input.product_type ||
                          "N/A"
                        }
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={12} sm={6} md={2}>
                      <InputParamCard
                        label="SKU"
                        value={item.input.sku || "N/A"}
                      />
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Forecast Data Points */}
          {item.forecast_adjusted && item.forecast_adjusted.length > 0 && (
            <Accordion
              defaultExpanded={false}
              sx={{
                boxShadow: "none",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                "&:before": {
                  display: "none",
                },
                "&.Mui-expanded": {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: 56,
                  "&.Mui-expanded": {
                    minHeight: 56,
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "16px 0",
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    pr: 1,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "Poppins",
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flex: 1,
                    }}
                  >
                    <TrendingUpIcon sx={{ fontSize: 20 }} />
                    Forecasted Inventory
                    <Chip
                      label={`${item.forecast_adjusted.length} periods`}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontFamily: "Poppins",
                        fontSize: "11px",
                        ml: 1,
                      }}
                    />
                  </Typography>
                  {item.explanation && showExplanation && (
                    <Tooltip
                      title={
                        displayExplanation
                          ? "Hide Forecast Rationale"
                          : "Why this forecast?"
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDisplayExplanation((prev) => !prev);
                        }}
                        sx={{
                          width: 28,
                          height: 28,
                          ml: 1,
                          bgcolor: displayExplanation
                            ? theme.palette.primary.main
                            : `${theme.palette.primary.main}14`,
                          color: displayExplanation
                            ? "#fff"
                            : theme.palette.primary.main,
                          border: `1.5px solid ${
                            displayExplanation
                              ? theme.palette.primary.main
                              : `${theme.palette.primary.main}40`
                          }`,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor: displayExplanation
                              ? theme.palette.primary.dark
                              : `${theme.palette.primary.main}28`,
                            transform: "scale(1.1)",
                          },
                        }}
                      >
                        <HelpOutlineIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2, pt: 0 }}>
                {/* AI Explanation Panel */}

                <Box
                  sx={{
                    maxHeight: "400px",
                    overflowY: "auto",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    p: 2,
                    bgcolor: theme.palette.background.paper,
                  }}
                >
                  <Grid container spacing={1}>
                    {item.forecast_adjusted.map((point, idx) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={Math.max(12 / item.forecast_adjusted.length, 2)}
                        key={idx}
                      >
                        <Paper
                          sx={{
                            p: 2,
                            borderRadius: 1,
                            border: `1px solid ${theme.palette.divider}`,
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? theme.palette.background.default
                                : theme.palette.grey[50],
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: theme.palette.action.hover,
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          <Box sx={{ textAlign: "center" }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily: "Poppins",
                                fontWeight: 600,
                                fontSize: "13px",
                                color: theme.palette.text.primary,
                                mb: 0.5,
                                lineHeight: 1.2,
                              }}
                            >
                              {new Date(point.date)?.toLocaleDateString(
                                "en-US",
                                {
                                  timeZone: "UTC",
                                  month: "short",
                                  day: "2-digit",
                                  year: "numeric",
                                },
                              )}
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{
                                fontFamily: "Poppins",
                                fontWeight: 700,
                                fontSize: "18px",
                                color: theme.palette.primary.main,
                                lineHeight: 1.2,
                              }}
                            >
                              {formatMetric(point.predicted_qty) + " units"}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
                {displayExplanation && item.explanation && (
                  <Box
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      overflow: "hidden",
                      border: `1.5px solid ${theme.palette.primary.main}30`,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}06, ${theme.palette.primary.main}02)`,
                    }}
                  >
                    {/* Header bar */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: 1.25,
                        borderBottom: `1px solid ${theme.palette.primary.main}20`,
                        bgcolor: `${theme.palette.primary.main}10`,
                      }}
                    >
                      <AutoAwesomeIcon
                        sx={{
                          fontSize: 16,
                          color: theme.palette.primary.main,
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: "Poppins",
                          fontWeight: 700,
                          fontSize: "13px",
                          color: theme.palette.primary.main,
                          letterSpacing: 0.3,
                          textTransform: "uppercase",
                        }}
                      >
                        Forecast Rationale
                      </Typography>
                      {/* <Chip
                        label="AI Generated"
                        size="small"
                        sx={{
                          ml: "auto",
                          fontFamily: "Poppins",
                          fontSize: "10px",
                          fontWeight: 600,
                          height: 20,
                          bgcolor: `${theme.palette.primary.main}18`,
                          color: theme.palette.primary.main,
                          border: `1px solid ${theme.palette.primary.main}30`,
                        }}
                      /> */}
                    </Box>
                    {/* HTML content */}
                    <Box
                      sx={{
                        px: 3,
                        py: 2,
                        maxHeight: 320,
                        overflowY: "auto",
                        "& h1, & h2, & h3, & h4": {
                          fontFamily: "Poppins",
                          fontWeight: 700,
                          color: theme.palette.text.primary,
                          mt: 1,
                          mb: 0.5,
                        },
                        "& p": {
                          fontFamily: "Poppins",
                          fontSize: "13px",
                          color: theme.palette.text.secondary,
                          lineHeight: 1.7,
                          mb: 1,
                          mt: 0,
                        },
                        "& ul, & ol": {
                          pl: 2.5,
                          mb: 1,
                        },
                        "& li": {
                          fontFamily: "Poppins",
                          fontSize: "13px",
                          color: theme.palette.text.secondary,
                          lineHeight: 1.7,
                          mb: 0.25,
                        },
                        "& strong, & b": {
                          color: theme.palette.text.primary,
                          fontWeight: 600,
                        },
                        "& table": {
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: "12px",
                          fontFamily: "Poppins",
                          mb: 1,
                        },
                        "& th": {
                          bgcolor: `${theme.palette.primary.main}14`,
                          color: theme.palette.primary.main,
                          fontWeight: 700,
                          p: "6px 10px",
                          textAlign: "left",
                          borderBottom: `2px solid ${theme.palette.primary.main}30`,
                        },
                        "& td": {
                          p: "5px 10px",
                          color: theme.palette.text.secondary,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        },
                        "& code": {
                          fontFamily: "monospace",
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? theme.palette.grey[800]
                              : theme.palette.grey[100],
                          borderRadius: "3px",
                          px: 0.5,
                          fontSize: "12px",
                        },
                      }}
                      dangerouslySetInnerHTML={{ __html: item.explanation }}
                    />
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          )}
          {item.forecast_adjusted && item.forecast_adjusted.length ? (
            <Accordion
              defaultExpanded={false}
              sx={{
                boxShadow: "none",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                "&:before": { display: "none" },
                "&.Mui-expanded": { margin: 0 },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={
                  {
                    // minHeight: 56,
                    // "&.Mui-expanded": { minHeight: 56 },
                    // "& .MuiAccordionSummary-content": {
                    //   margin: "16px 0",
                    // },
                  }
                }
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <TimelineIcon sx={{ fontSize: 20 }} />
                  Inventory by Region
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3, pt: 0 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ overflowX: "auto", display: "flex" }}>
                      {[
                        {
                          warehouse: "West Coast",
                          percent: 28,
                        },
                        {
                          warehouse: "South Central",
                          percent: 18,
                        },
                        {
                          warehouse: "East Coast",
                          percent: 32,
                        },
                        {
                          warehouse: "Midwest",
                          percent: 22,
                        },
                      ].map((entry, idx) => (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                          // gap={1}
                          key={entry.warehouse + idx}
                        >
                          <Paper
                            sx={{
                              p: 0.75,
                              borderRadius: 0.75,
                              border: `1px solid ${theme.palette.divider}`,
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? theme.palette.background.default
                                  : theme.palette.grey[50],
                              transition: "all 0.2s ease",
                              mr: 1,
                              "&:hover": {
                                bgcolor: theme.palette.action.hover,
                                transform: "translateY(-1px)",
                              },
                            }}
                          >
                            <Box sx={{ textAlign: "center" }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontFamily: "Poppins",
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  color: theme.palette.text.primary,
                                  mb: 0.125,
                                  lineHeight: 1.1,
                                }}
                              >
                                {entry.warehouse}
                              </Typography>

                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: "Poppins",
                                  fontWeight: 700,
                                  fontSize: "18px",
                                  color: theme.palette.primary.main,
                                  lineHeight: 1.1,
                                }}
                              >
                                {formatMetric(
                                  (item.forecast_adjusted.reduce(
                                    (a, b) => a + Number(b.predicted_qty || 0),
                                    0,
                                  ) *
                                    entry.percent) /
                                    100,
                                )}
                              </Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ) : (
            <></>
          )}
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  const isProductForecast =
    remainingItems.length > 0 &&
    remainingItems[0]?.input?.forecast_by?.toLowerCase() === "product";

  const isSimilarProductForecast =
    forecastBy?.toLowerCase() === "similar product";

  const productGroups = React.useMemo(() => {
    if (!isProductForecast) return null;
    const map = {};
    remainingItems.forEach((item) => {
      const pid =
        item.input?.product?._id ||
        item.input?.product ||
        item.input?.sku ||
        "unknown";
      const pname = item.input?.product?.name || item.input?.product || pid;
      if (!map[pid]) map[pid] = { id: pid, name: pname, items: [] };
      map[pid].items.push(item);
    });
    return Object.values(map);
  }, [remainingItems, isProductForecast]);

  console.log(productGroups);

  const isMultiProductForecast =
    isProductForecast && (productGroups?.length || 0) > 1;

  const getProductViewMode = (idx) => productViewModes[idx] ?? "product";
  const setProductTabViewMode = (idx, mode) =>
    setProductViewModes((prev) => ({ ...prev, [idx]: mode }));

  // Items the footer button acts on when in product-summary view
  const activeGroupItems = React.useMemo(() => {
    if (!isProductForecast) return null;
    if (isMultiProductForecast) {
      return productGroups?.[activeProductTab]?.items || [];
    }
    return remainingItems;
  }, [
    isProductForecast,
    isMultiProductForecast,
    productGroups,
    activeProductTab,
    remainingItems,
  ]);

  // Determine the single item currently visible (for per-SKU Accept/Discard)
  const activeForecastItem = React.useMemo(() => {
    if (isSimilarProductForecast) {
      return (
        remainingItems[Math.min(activeTab, remainingItems.length - 1)] || null
      );
    }
    if (!isProductForecast) return forecastData;
    if (isMultiProductForecast) {
      const group = productGroups?.[activeProductTab];
      const viewMode = getProductViewMode(activeProductTab);
      if (viewMode === "sku") {
        const items = group?.items || [];
        return items[Math.min(activeTab, items.length - 1)] || null;
      }
      return null; // product summary view — show Accept All
    }
    // single-product forecast
    if (productViewMode === "sku") {
      return remainingItems[activeTab] || null;
    }
    return null; // product summary view — show Accept All
  }, [
    isSimilarProductForecast,
    isProductForecast,
    isMultiProductForecast,
    productGroups,
    activeProductTab,
    productViewModes,
    activeTab,
    productViewMode,
    forecastData,
    remainingItems,
  ]);

  const computeProductSummary = (items, label) => {
    if (!items?.length) return null;
    const validItems = items.filter((i) => i && i.metrics);
    if (!validItems.length) return null;
    const metrics = {
      total_on_hand: validItems.reduce(
        (s, i) => s + (i.metrics.total_on_hand || 0),
        0,
      ),
      avg_daily_demand: validItems.reduce(
        (s, i) => s + (i.metrics.avg_daily_demand || 0),
        0,
      ),
      safety_stock_used: validItems.reduce(
        (s, i) => s + (i.metrics.safety_stock_used || 0),
        0,
      ),
      recommended_order_qty: validItems.reduce(
        (s, i) => s + (i.metrics.recommended_order_qty || 0),
        0,
      ),
      reorder_point: validItems.reduce(
        (s, i) => s + (i.metrics.reorder_point || 0),
        0,
      ),
      lead_time_days: Math.round(
        validItems.reduce((s, i) => s + (i.metrics.lead_time_days || 0), 0) /
          validItems.length,
      ),
      total_in_transit: validItems.reduce(
        (s, i) => s + (i.metrics.total_in_transit || 0),
        0,
      ),
    };
    const dates = validItems
      .map((i) => i.metrics.reorder_by_date)
      .filter(Boolean);
    if (dates.length) {
      metrics.reorder_by_date = new Date(
        Math.min(...dates.map((d) => new Date(d).getTime())),
      ).toISOString();
    }
    const periodMap = {};
    validItems.forEach((item) => {
      (item.forecast_adjusted || []).forEach((pt) => {
        if (!periodMap[pt.date])
          periodMap[pt.date] = { date: pt.date, predicted_qty: 0 };
        periodMap[pt.date].predicted_qty += Number(pt.predicted_qty || 0);
      });
    });
    const forecast_adjusted = Object.values(periodMap).sort(
      (a, b) => new Date(a.date) - new Date(b.date),
    );
    return {
      metrics,
      forecast_adjusted,
      input: {
        ...(validItems[0]?.input || {}),
        sku: `${label || forecastProductName || "Product"} — All SKUs`,
      },
    };
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: "80vh",
          maxHeight: "90vh",
        },
      }}
    >
      {/* Enhanced Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}08, ${theme.palette.primary.main}04)`,
          borderBottom: `1px solid ${theme.palette.divider}`,
          p: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: 48,
                height: 48,
              }}
            >
              <TrendingUpIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mb: 0.5,
                }}
              >
                Forecast Results
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  {isProductLoading || forecastProductName
                    ? `Product: ${currentLabel || forecastProductName}`
                    : `SKU: ${currentLabel}`}
                </Typography>
                <Chip
                  label={
                    forecastProductName || isProductLoading
                      ? "Product Forecast"
                      : "Latest Forecast"
                  }
                  size="small"
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "11px",
                    fontWeight: 500,
                  }}
                />
              </Box>
              {sku && !isProductLoading && currentProductName && (
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 400,
                    color: theme.palette.text.secondary,
                  }}
                >
                  {`Product: ${currentProductName}`}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <IconButton
              onClick={() => onClose()}
              sx={{
                bgcolor: "var(--bg-primary)",
                "&:hover": {
                  bgcolor: "var(--hover-bg)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <DialogContent
        sx={{
          p: 0,
          bgcolor:
            theme.palette.mode === "dark"
              ? theme.palette.background.default
              : theme.palette.grey[50],
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "60vh",
              p: 4,
              textAlign: "center",
            }}
          >
            {/* Header Section */}
            <Box sx={{ mb: 4, textAlign: "center" }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.primary.main}10)`,
                  border: `2px solid ${theme.palette.primary.main}30`,
                  mb: 3,
                }}
              >
                <CircularProgress
                  size={40}
                  thickness={4}
                  sx={{ color: theme.palette.primary.main }}
                />
              </Box>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mb: 1,
                }}
              >
                Generating Forecast
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "Poppins",
                  color: theme.palette.text.secondary,
                  maxWidth: 400,
                  mx: "auto",
                }}
              >
                The AI agent is analyzing historical data and creating accurate
                predictions for your inventory
              </Typography>
            </Box>

            {/* Progress Steps - Grid Layout */}
            <Box sx={{ width: "100%", maxWidth: 800, mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mb: 3,
                  textAlign: "center",
                }}
              >
                Processing Steps
              </Typography>

              <Grid container spacing={2}>
                {forecastSteps.map((step, index) => (
                  <Grid item xs={12} sm={6} md={4} key={step.label}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                        p: 3,
                        borderRadius: 2,
                        bgcolor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        transition: "all 0.3s ease",
                        height: "100%",
                        minHeight: 140,
                        ...(index < currentStep && {
                          bgcolor: theme.palette.success.main + "08",
                          borderColor: theme.palette.success.main + "30",
                        }),
                        ...(index === currentStep && {
                          bgcolor: theme.palette.primary.main + "08",
                          borderColor: theme.palette.primary.main + "30",
                          boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
                        }),
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          flexShrink: 0,
                          width: 40,
                          height: 40,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {index < currentStep ? (
                          <CheckCircleIcon
                            sx={{
                              color: theme.palette.success.main,
                              fontSize: 40,
                            }}
                          />
                        ) : index === currentStep ? (
                          <Box sx={{ position: "relative" }}>
                            <CircularProgress
                              size={40}
                              thickness={4}
                              variant="determinate"
                              value={stepProgress}
                              sx={{
                                color: theme.palette.primary.main,
                              }}
                            />
                            <Box
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: 40,
                                height: 40,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  color: theme.palette.primary.main,
                                }}
                              >
                                {Math.round(stepProgress)}%
                              </Typography>
                            </Box>
                          </Box>
                        ) : (
                          <RadioButtonUncheckedIcon
                            sx={{
                              color: theme.palette.grey[400],
                              fontSize: 40,
                            }}
                          />
                        )}
                      </Box>

                      <Box sx={{ flex: 1, textAlign: "center" }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            mb: 1,
                            fontSize: "14px",
                          }}
                        >
                          {step.label}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Poppins",
                            color: theme.palette.text.secondary,
                            fontSize: "12px",
                            lineHeight: 1.4,
                          }}
                        >
                          {step.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Time Estimate */}
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Poppins",
                  color: theme.palette.text.secondary,
                  fontStyle: "italic",
                }}
              >
                {`This process typically takes around ${Math.round(expectedTime / 60) || "2"} minutes to complete`}
              </Typography>
            </Box>
          </Box>
        ) : remainingItems.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "400px",
              p: 4,
            }}
          >
            <AssessmentIcon
              sx={{ fontSize: 64, color: theme.palette.grey[400], mb: 2 }}
            />
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
                color: theme.palette.text.secondary,
                mb: 1,
              }}
            >
              No Forecast Data Available
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Poppins",
                color: theme.palette.text.secondary,
                textAlign: "center",
              }}
            >
              Unable to load forecast results. Please try again.
            </Typography>
          </Box>
        ) : isMultiProductForecast ? (
          /* ─── Multi-product grouped view ─── */
          <Box>
            {/* Outer product tabs */}
            <Tabs
              value={activeProductTab}
              onChange={(e, v) => {
                setActiveProductTab(v);
                setActiveTab(0);
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.background.paper,
                px: 3,
                "& .MuiTab-root": {
                  fontFamily: "Poppins",
                  fontWeight: 700,
                  textTransform: "none",
                  minHeight: 48,
                  fontSize: 14,
                },
              }}
            >
              {productGroups.map((g, i) => (
                <Tab key={g.id} label={`Product: ${g.name}`} />
              ))}
            </Tabs>

            {/* Per-product: view-mode toggle */}
            {(() => {
              const group = productGroups[activeProductTab];
              const viewMode = getProductViewMode(activeProductTab);
              const skuItems = group?.items || [];
              return (
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 3,
                      py: 1.5,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      bgcolor: theme.palette.background.paper,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: "Poppins",
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        mr: 1,
                      }}
                    >
                      View:
                    </Typography>
                    <Button
                      size="small"
                      variant={
                        viewMode === "product" ? "contained" : "outlined"
                      }
                      onClick={() =>
                        setProductTabViewMode(activeProductTab, "product")
                      }
                      startIcon={<ViewModuleIcon />}
                      sx={{
                        textTransform: "none",
                        fontFamily: "Poppins",
                        fontWeight: 600,
                        fontSize: 13,
                        borderRadius: 2,
                      }}
                    >
                      Product Summary
                    </Button>
                    <Button
                      size="small"
                      variant={viewMode === "sku" ? "contained" : "outlined"}
                      onClick={() =>
                        setProductTabViewMode(activeProductTab, "sku")
                      }
                      startIcon={<ViewListIcon />}
                      sx={{
                        textTransform: "none",
                        fontFamily: "Poppins",
                        fontWeight: 600,
                        fontSize: 13,
                        borderRadius: 2,
                      }}
                    >
                      SKU Detail ({skuItems.length})
                    </Button>
                  </Box>

                  {/* SKU detail tabs (only when viewMode === sku) */}
                  {viewMode === "sku" && (
                    <Tabs
                      value={Math.min(activeTab, skuItems.length - 1)}
                      onChange={(e, v) => setActiveTab(v)}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        bgcolor: theme.palette.background.paper,
                        px: 3,
                        "& .MuiTab-root": {
                          fontFamily: "Poppins",
                          fontWeight: 600,
                          textTransform: "none",
                          minHeight: 48,
                        },
                      }}
                    >
                      {skuItems.map((item, i) => (
                        <Tab
                          key={i}
                          label={
                            <Box sx={{ textAlign: "left" }}>
                              <Typography
                                sx={{
                                  fontFamily: "Poppins",
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  lineHeight: 1.2,
                                }}
                              >
                                {`SKU: ${item.sku || item.product?.sku || `Item ${i + 1}`}`}
                              </Typography>
                              {item.input?.product?.name && (
                                <Typography
                                  sx={{
                                    fontFamily: "Poppins",
                                    fontWeight: 400,
                                    fontSize: "11px",
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {`Product: ${item.input.product.name}`}
                                </Typography>
                              )}
                            </Box>
                          }
                          sx={{ alignItems: "flex-start" }}
                        />
                      ))}
                    </Tabs>
                  )}

                  <Box sx={{ p: 3 }}>
                    {viewMode === "product"
                      ? renderForecastCard(
                          computeProductSummary(skuItems, group?.name),
                          0,
                        )
                      : renderForecastCard(
                          skuItems[Math.min(activeTab, skuItems.length - 1)],
                          Math.min(activeTab, skuItems.length - 1),
                        )}
                  </Box>
                </Box>
              );
            })()}
          </Box>
        ) : remainingItems.length > 1 ? (
          /* ─── Single-product or SKU-level bulk forecast ─── */
          <Box>
            {isProductForecast && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 3,
                  py: 1.5,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.background.paper,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    mr: 1,
                  }}
                >
                  View:
                </Typography>
                <Button
                  size="small"
                  variant={
                    productViewMode === "product" ? "contained" : "outlined"
                  }
                  onClick={() => setProductViewMode("product")}
                  startIcon={<ViewModuleIcon />}
                  sx={{
                    textTransform: "none",
                    fontFamily: "Poppins",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: 2,
                  }}
                >
                  Product Summary
                </Button>
                <Button
                  size="small"
                  variant={productViewMode === "sku" ? "contained" : "outlined"}
                  onClick={() => setProductViewMode("sku")}
                  startIcon={<ViewListIcon />}
                  sx={{
                    textTransform: "none",
                    fontFamily: "Poppins",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: 2,
                  }}
                >
                  SKU Detail ({remainingItems.length})
                </Button>
              </Box>
            )}
            {(!isProductForecast || productViewMode === "sku") && (
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.background.paper,
                  px: 3,
                  "& .MuiTab-root": {
                    fontFamily: "Poppins",
                    fontWeight: 600,
                    textTransform: "none",
                    minHeight: 48,
                  },
                }}
              >
                {remainingItems.map((item, index) => (
                  <Tab
                    key={index}
                    label={
                      <Box sx={{ textAlign: "left" }}>
                        <Typography
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            fontSize: "14px",
                            lineHeight: 1.2,
                          }}
                        >
                          {`SKU: ${item.sku || item.product?.sku || `Item ${index + 1}`}`}
                        </Typography>
                        {item.input?.product?.name && (
                          <Typography
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 400,
                              fontSize: "11px",
                              color: theme.palette.text.secondary,
                              lineHeight: 1.2,
                            }}
                          >
                            {`Product: ${item.input.product.name}`}
                          </Typography>
                        )}
                      </Box>
                    }
                    sx={{ alignItems: "flex-start" }}
                  />
                ))}
              </Tabs>
            )}
            <Box sx={{ p: 3 }}>
              {isProductForecast && productViewMode === "product"
                ? renderForecastCard(computeProductSummary(remainingItems), 0)
                : renderForecastCard(
                    remainingItems[
                      Math.min(activeTab, remainingItems.length - 1)
                    ],
                    Math.min(activeTab, remainingItems.length - 1),
                  )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>{renderForecastCard(remainingItems[0], 0)}</Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor:
            theme.palette.mode === "dark"
              ? theme.palette.background.default
              : theme.palette.grey[50],
          justifyContent: "space-between",
        }}
      >
        {/* Left: SKU Navigation */}
        <Box>
          {!isProductForecast && skuList.length > 1 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tooltip title="Previous">
                <span>
                  <IconButton
                    size="large"
                    onClick={() =>
                      loadDataBySku(skuList[Math.max(0, displayIndex - 1)])
                    }
                    disabled={displayIndex <= 0}
                    sx={{
                      width: 50,
                      height: 50,
                      bgcolor: "white",
                      border: `1px solid ${theme.palette.divider}`,
                      color: theme.palette.text.primary,
                    }}
                  >
                    <NavigateBeforeIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Box sx={{ textAlign: "center", px: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    fontSize: "16px",
                    marginLeft: 3,
                    marginRight: 3,
                  }}
                >
                  {displayIndex + 1} of {totalSkus}
                </Typography>
              </Box>

              <Tooltip title="Next">
                <span>
                  <IconButton
                    size="large"
                    onClick={() =>
                      loadDataBySku(
                        skuList[Math.min(totalSkus - 1, displayIndex + 1)],
                      )
                    }
                    disabled={displayIndex >= totalSkus - 1}
                    sx={{
                      width: 50,
                      height: 50,
                      bgcolor: "white",
                      border: `1px solid ${theme.palette.divider}`,
                      color: theme.palette.text.primary,
                    }}
                  >
                    <NavigateNextIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Right: Action Buttons */}
        {/* {forecastType === "product" ? (
          <></>
        ) : ( */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Button
            onClick={async () => {
              if (isSimilarProductForecast) {
                if (!activeForecastItem) return;
                const originalItem = forecastItems[0];
                try {
                  await acceptMetricsSilent({
                    ...activeForecastItem,
                    sku: originalItem.sku,
                    client: originalItem.client,
                  });
                  toast.success(
                    `${originalItem.sku} updated with forecast metrics`,
                  );
                } catch {
                  toast.error("Failed to accept forecast metrics");
                  return;
                }
                closeForecast();
                return;
              }
              if (activeForecastItem) {
                // Per-SKU accept
                try {
                  await acceptMetricsSilent(activeForecastItem);
                  toast.success(`${activeForecastItem.sku} accepted`);
                } catch {
                  toast.error("Failed to accept SKU");
                  return;
                }
                removeItemsFromRemaining([activeForecastItem]);
              } else {
                // Product summary accept (current group or all remaining)
                const items = activeGroupItems || remainingItems;
                try {
                  await Promise.all(items.map((i) => acceptMetricsSilent(i)));
                  const groupName = isMultiProductForecast
                    ? productGroups?.[activeProductTab]?.name
                    : null;
                  toast.success(
                    groupName
                      ? `${groupName}: all SKUs accepted`
                      : "All SKUs accepted",
                  );
                } catch {
                  toast.error("Failed to accept");
                  return;
                }
                removeItemsFromRemaining(items);
              }
            }}
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{
              textTransform: "none",
              fontFamily: "Poppins",
              fontWeight: 600,
              px: 3,
              py: 1.5,
              borderRadius: 2,
              mr: 2,
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Processing...
              </Box>
            ) : isSimilarProductForecast ? (
              "Accept"
            ) : isProductForecast &&
              !activeForecastItem &&
              remainingItems.length > 1 ? (
              "Accept All"
            ) : (
              "Accept"
            )}
          </Button>
          <Button
            onClick={async () => {
              if (isSimilarProductForecast) {
                if (!activeForecastItem) return;
                try {
                  await discardMetricsSilent(activeForecastItem);
                } catch {
                  console.error("Discard failed");
                }
                removeItemsFromRemaining([activeForecastItem]);
                return;
              }
              if (activeForecastItem) {
                // Per-SKU discard
                try {
                  await discardMetricsSilent(activeForecastItem);
                } catch {
                  console.error("Discard failed");
                }
                removeItemsFromRemaining([activeForecastItem]);
              } else {
                // Product summary discard (current group or all remaining)
                const items = activeGroupItems || remainingItems;
                try {
                  await Promise.all(items.map((i) => discardMetricsSilent(i)));
                } catch {
                  console.error("Discard all failed");
                }
                removeItemsFromRemaining(items);
              }
            }}
            variant="outlined"
            disabled={loading}
            sx={{
              textTransform: "none",
              fontFamily: "Poppins",
              fontWeight: 600,
              px: 3,
              py: 1.5,
              borderRadius: 2,
              opacity: loading ? 0.6 : 1,
            }}
          >
            {isSimilarProductForecast
              ? "Discard"
              : isProductForecast &&
                  !activeForecastItem &&
                  remainingItems.length > 1
                ? "Discard All"
                : "Discard"}
          </Button>
        </Box>
        {/* )} */}
      </DialogActions>
    </Dialog>
  );
};

export default ForecastResultModal;
